import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Offcanvas, Button, ButtonGroup, Form } from 'react-bootstrap';
import defaultModeImg from '@/assets/img/mizfa-tools-white-min.png';
import darkModeImg from '@/assets/img/mizfa-tools-dark-min.png';
import Flex from '@/components/common/Flex';
import RadioItem from './RadioItem';
import { useAppContext } from '@/Main';
import { titleReturner } from '@/app/Settings/pages/Appearance';
import ChildrensTooltip from '../common/ChildrensTooltip';
import type { NavbarPosition } from '@/config';
import {
  AiSupportIcon,
  useAiSupportPlace,
  writeAiSupportPlace
} from '@/components/navbar/AiSupportFab';

const titleReturner2 = (title: string) => {
  switch (title) {
    case 'left':
      return 'چپ صفحه';
    case 'none':
      return 'مخفی';
    case 'right':
      return 'راست صفحه';
    default:
      return undefined;
  }
};

const SettingsPanel = () => {
  const {
    config: {
      isFluid,
      navbarPosition,
      themeVariant,
      theme,
      showSettingPanel,
      disabledNavbarPosition
    },
    setConfig,
    changeTheme,
    changeThemeVariant,
    configDispatch
  } = useAppContext();
  const aiSupportPlace = useAiSupportPlace();

  return (
    <Offcanvas
      show={showSettingPanel}
      onHide={() => setConfig('showSettingPanel', false)}
      placement="end"
      style={{ maxWidth: '22rem' }}
      className="border-0 settings-appearance-drawer"
    >
      <Offcanvas.Header
        closeButton
        closeVariant="white"
        className="bg-shape settings-panel-header"
      >
        <Offcanvas.Title as="div" className="py-1 z-1 light">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <div className="fs-0 fw-medium text-white mb-0">
              <FontAwesomeIcon icon="palette" className="me-2" />
              تنظیمات ظاهری
            </div>
            <ChildrensTooltip html="تنظیمات پیش‌فرض" placement="bottom">
              <Button
                variant="secondary"
                size="sm"
                className="rounded-pill p-1 d-flex align-items-center justify-content-center fs--2 ms-1 mt-0 mb-0"
                onClick={() => {
                  configDispatch({ type: 'RESET' });
                }}
              >
                <FontAwesomeIcon icon="redo-alt" />
              </Button>
            </ChildrensTooltip>
          </div>
          <p className="mb-0 fs--1 text-white opacity-75" />
        </Offcanvas.Title>
      </Offcanvas.Header>
      <style>{`
        .settings-appearance-drawer .btn-group-navbar-style .btn,
        .settings-appearance-drawer .form-check-label {
          font-weight: 400;
          font-size: inherit;
        }
      `}</style>
      <Offcanvas.Body className="scrollbar fs--1">
        <div className="fw-normal text-700 mb-2">حالت صفحه نمایش</div>

        <ButtonGroup className="btn-group-navbar-style">
          <RadioItem
            name="theme-mode-1"
            label="حالت روز"
            active={theme === 'light'}
            onChange={() => changeTheme('light')}
            image={defaultModeImg}
          />
          <RadioItem
            name="theme-mode-2"
            label="حالت شب"
            active={theme === 'dark'}
            onChange={() => changeTheme('dark')}
            image={darkModeImg}
          />
        </ButtonGroup>

        <hr />
        <div className="fw-normal text-700 mb-2">رنگ پنل کاربری</div>
        <div className="d-flex flex-nowrap gap-1">
          {[
            { variant: 'primary', label: 'پیش‌فرض', color: '#e2cc9e' },
            { variant: 'teal', label: 'سبز آبی', color: '#02928b' },
            { variant: 'green', label: 'سبز', color: '#10b981' },
            { variant: 'blue', label: 'آبی', color: '#3b82f6' },
            { variant: 'purpureus', label: 'بنفش', color: '#a855f7' },
            { variant: 'yellow', label: 'زرد', color: '#dfba03' },
            { variant: 'orange', label: 'نارنجی', color: '#f97316' },
            { variant: 'red', label: 'قرمز', color: '#ef4444' },
            { variant: 'pink', label: 'صورتی', color: '#ec4899' }
          ].map(({ variant, color, label }) => (
            <div key={variant}>
              <ChildrensTooltip
                html={label}
                showOnHover={false}
                delayToRemove={100}
                placement="top"
              >
                <Form.Check
                  inline
                  checked={themeVariant === variant}
                  onChange={() => {
                    changeThemeVariant(variant);
                  }}
                  name="group2"
                  className={`theme-changer-${variant}`}
                  type="radio"
                />
              </ChildrensTooltip>
              <style>{`
              .theme-changer-${variant} {margin:0;}
              .theme-changer-${variant} .form-check-input[type=radio] {background-color:${color};cursor:pointer;width:1.7rem;height:1.7rem;padding:0;margin:0;}
              .theme-changer-${variant} .form-check-input[type=radio]:checked {background-color:${color} !important;background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20height%3D%2210%22%20width%3D%228.75%22%20viewBox%3D%220%200%20448%20512%22%3E%3C!--!Font%20Awesome%20Free%206.5.2%20by%20%40fontawesome%20-%20https%3A%2F%2Ffontawesome.com%20License%20-%20https%3A%2F%2Ffontawesome.com%2Flicense%2Ffree%20Copyright%202024%20Fonticons%2C%20Inc.--%3E%3Cpath%20fill%3D%22%23ffffff%22%20d%3D%22M438.6%20105.4c12.5%2012.5%2012.5%2032.8%200%2045.3l-256%20256c-12.5%2012.5-32.8%2012.5-45.3%200l-128-128c-12.5-12.5-12.5-32.8%200-45.3s32.8-12.5%2045.3%200L160%20338.7%20393.4%20105.4c12.5-12.5%2032.8-12.5%2045.3%200z%22%2F%3E%3C%2Fsvg%3E"); !important;background-size: 0.8rem;}
          `}</style>
            </div>
          ))}
        </div>
        <hr />

        <Flex justifyContent="between">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            className="me-2 flex-shrink-0"
            aria-hidden="true"
            style={{ color: 'var(--falcon-primary)' }}
          >
            <path
              fill="currentColor"
              d="M21.92,11.62a1,1,0,0,0-.21-.33l-4-4a1,1,0,1,0-1.42,1.42L18.59,11H5.41l2.3-2.29A1,1,0,1,0,6.29,7.29l-4,4a1,1,0,0,0-.21.33,1,1,0,0,0,0,.76,1,1,0,0,0,.21.33l4,4a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L5.41,13H18.59l-2.3,2.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0l4-4a1,1,0,0,0,.21-.33A1,1,0,0,0,21.92,11.62Z"
            />
          </svg>
          <div className="flex-1 fw-normal text-700">
            تمام عرض کردن پنل کاربری
          </div>
          <Form.Check
            type="switch"
            id="fluid-mode-switch"
            checked={isFluid}
            onChange={({ target }) => setConfig('isFluid', target.checked)}
          />
        </Flex>
        <hr />

        <div>
          <Flex alignItems="center" className="mb-2">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              className="me-2 flex-shrink-0"
              aria-hidden="true"
              style={{ color: 'var(--falcon-primary)' }}
            >
              <path
                fill="currentColor"
                d="M13,13.5H3a1,1,0,0,0,0,2H13a1,1,0,0,0,0-2Zm8-5H3a1,1,0,0,0,0,2H21a1,1,0,0,0,0-2Z"
              />
            </svg>
            <div className="fw-normal text-700">محل منوی پنل کاربری</div>
          </Flex>
          <Form.Select
            className="w-50 mb-2"
            size="sm"
            defaultValue={navbarPosition}
            onChange={({ target }) =>
              setConfig('navbarPosition', target.value as NavbarPosition)
            }
          >
            {(['vertical', 'top', 'combo'] as const).map(option => (
              <option
                key={option}
                disabled={disabledNavbarPosition.includes(option)}
                value={option}
              >
                {titleReturner(option)}
              </option>
            ))}
          </Form.Select>
        </div>
        <hr />
        <div>
          <Flex alignItems="center" className="mb-2">
            <span
              className="me-2 d-inline-flex"
              style={{ color: 'var(--falcon-primary, #2c7be5)' }}
            >
              <AiSupportIcon size={16} />
            </span>
            <div className="fw-normal text-700">محل قرارگیری پشتیبان AI</div>
          </Flex>
          <Form.Select
            className="w-50"
            size="sm"
            value={aiSupportPlace}
            onChange={({ target }) => {
              writeAiSupportPlace(target.value);
            }}
          >
            {['left', 'right', 'none'].map(option => (
              <option key={option} value={option}>
                {titleReturner2(option)}
              </option>
            ))}
          </Form.Select>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default SettingsPanel;
